import { useEffect, useRef, useState } from "react";
import { scrollState, onPhaseChange } from "../state/scroll";
import { useContent } from "../state/siteContent";

export default function ReservationPhase() {
  const sectionRef = useRef(null);
  const { home, settings } = useContent();
  const PLOTS_AVAILABLE = Number(home.resCount) || 0; // Combined across projects
  const [phase, setPhase] = useState(scrollState.phase);
  const [inView, setInView] = useState(false);
  const [counterValue, setCounterValue] = useState(0);

  useEffect(() => onPhaseChange(setPhase), []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setInView(true);
      }),
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const startTs = performance.now();
    let raf;
    const tick = (ts) => {
      const elapsed = ts - startTs;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const val = Math.round(start + (PLOTS_AVAILABLE - start) * eased);
      setCounterValue(val);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, PLOTS_AVAILABLE]);

  if (phase !== "PLOT") return null;

  return (
    <section ref={sectionRef} className="reservation-phase">
      <style>{`
        .reservation-phase {
          position: relative;
          min-height: 100vh;
          min-height: 100svh;
          width: 100%;
          background: linear-gradient(180deg, #04110a 0%, #08101e 50%, #04110a 100%);
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(60px, 10vh, 100px) clamp(16px, 4vw, 24px) clamp(40px, 6vh, 60px);
          overflow: hidden;
        }
        .reservation-phase::before {
          content: "";
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse at 50% 40%, rgba(212, 164, 92,0.08), transparent 60%),
            radial-gradient(ellipse at 20% 80%, rgba(212, 164, 92,0.04), transparent 50%);
          pointer-events: none;
        }
        .reservation-frame {
          position: relative;
          width: min(960px, 100%);
          padding: clamp(40px, 8vh, 80px) clamp(20px, 5vw, 60px);
          z-index: 2;
        }
        .reservation-frame::before,
        .reservation-frame::after {
          content: "";
          position: absolute;
          width: clamp(20px, 3vw, 28px); height: clamp(20px, 3vw, 28px);
          border: 1px solid rgba(212, 164, 92,0.5);
        }
        .reservation-frame::before {
          top: 0; left: 0;
          border-right: none; border-bottom: none;
        }
        .reservation-frame::after {
          bottom: 0; right: 0;
          border-left: none; border-top: none;
        }

        .reservation-content {
          display: flex; flex-direction: column;
          align-items: center;
          text-align: center;
          gap: clamp(16px, 2.5vh, 24px);
        }

        .res-lbl {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(9px, 1.2vw, 10px);
          letter-spacing: 0.5em;
          color: rgba(212, 164, 92,0.7); text-transform: uppercase;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 700ms ease, transform 700ms ease;
        }
        .reservation-phase.in-view .res-lbl {
          opacity: 1; transform: translateY(0);
        }

        .res-counter {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-weight: 300;
          font-size: clamp(3rem, 9vw, 7rem);
          line-height: 0.95;
          color: #f4f0e4;
          letter-spacing: 0.02em;
          margin: 0;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 900ms ease 200ms, transform 900ms ease 200ms;
        }
        .reservation-phase.in-view .res-counter {
          opacity: 1; transform: translateY(0);
        }
        .res-counter em {
          font-style: italic;
          background: linear-gradient(120deg, #d4a45c, #eccb92, #d4a45c);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          display: block;
          font-size: clamp(4rem, 11vw, 9rem);
          line-height: 1;
        }
        .res-counter-sub {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-weight: 300;
          font-size: clamp(1.2rem, 2.4vw, 2rem);
          color: #f4f0e4;
          letter-spacing: 0.04em;
          margin: -10px 0 0 0;
          font-style: italic;
        }

        .res-divider {
          width: 80px; height: 1px;
          background: linear-gradient(90deg, transparent, #d4a45c, transparent);
          margin: 12px 0;
          opacity: 0;
          transform: scaleX(0);
          transition: opacity 700ms ease 400ms, transform 700ms ease 400ms;
        }
        .reservation-phase.in-view .res-divider {
          opacity: 1; transform: scaleX(1);
        }

        .res-body {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-style: italic;
          font-size: clamp(0.95rem, 1.6vw, 1.15rem);
          line-height: 1.7;
          color: rgba(244,240,228,0.7);
          max-width: 520px;
          margin: 0;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 700ms ease 600ms, transform 700ms ease 600ms;
          padding: 0 8px;
        }
        .reservation-phase.in-view .res-body {
          opacity: 1; transform: translateY(0);
        }

        .res-actions {
          display: flex; flex-direction: column;
          align-items: center; gap: 18px;
          margin-top: 18px;
          width: 100%;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 800ms ease 800ms, transform 800ms ease 800ms;
        }
        .reservation-phase.in-view .res-actions {
          opacity: 1; transform: translateY(0);
        }

        .res-btn {
          position: relative;
          padding: clamp(14px, 2vh, 18px) clamp(32px, 5vw, 52px);
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(10px, 1.4vw, 11px);
          letter-spacing: 0.32em;
          text-transform: uppercase;
          font-weight: 500;
          color: #1a1208;
          background: linear-gradient(120deg, #d4a45c, #eccb92, #d4a45c);
          background-size: 200% 100%;
          background-position: 0% 50%;
          border: none;
          border-radius: 0;
          cursor: pointer;
          display: inline-flex; align-items: center; gap: 14px;
          text-decoration: none;
          box-shadow: 0 8px 28px rgba(212, 164, 92,0.25);
          transition: all 400ms cubic-bezier(0.4, 0, 0.2, 1);
          min-height: 52px;
        }
        .res-btn:hover, .res-btn:active {
          background-position: 100% 50%;
          transform: translateY(-2px);
          box-shadow:
            0 18px 44px rgba(212, 164, 92,0.4),
            0 0 0 1px rgba(212, 164, 92,0.6);
        }
        .res-btn__arrow {
          transition: transform 400ms ease;
        }
        .res-btn:hover .res-btn__arrow {
          transform: translateX(6px);
        }

        .res-secondary {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(9px, 1.2vw, 10px);
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: rgba(244,240,228,0.5);
          display: flex; align-items: center; gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .res-secondary a {
          color: rgba(212, 164, 92,0.85);
          text-decoration: none;
          transition: color 200ms ease;
          /* Better tap target */
          padding: 8px 4px;
        }
        .res-secondary a:hover {
          color: #eccb92;
        }
        .res-secondary__sep {
          width: 3px; height: 3px; border-radius: 50%;
          background: rgba(212, 164, 92,0.4);
        }
        @media (max-width: 480px) {
          .res-secondary { gap: 8px; }
          .res-secondary > span { display: none; }
        }

        .res-footer {
          margin-top: clamp(40px, 6vh, 60px);
          padding-top: clamp(20px, 3vh, 28px);
          width: 100%;
          border-top: 1px solid rgba(244,240,228,0.08);
          text-align: center;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(8px, 1.1vw, 9px);
          letter-spacing: 0.32em;
          color: rgba(244,240,228,0.35);
          text-transform: uppercase;
          opacity: 0;
          transition: opacity 800ms ease 1100ms;
        }
        .reservation-phase.in-view .res-footer {
          opacity: 1;
        }
        .res-footer span {
          color: rgba(212, 164, 92,0.5);
          margin: 0 10px;
        }
      `}</style>

      <div className="reservation-frame">
        <div className="reservation-content">
          <div className="res-lbl">{home.resLabel}</div>

          <div>
            <div className="res-counter">
              <em>{counterValue.toLocaleString("en-IN")}+</em>
            </div>
            <div className="res-counter-sub">{home.resCountSub}</div>
          </div>

          <div className="res-divider" />

          <p className="res-body">
            {home.resBody}
          </p>

          <div className="res-actions">
            <a href="/contact" className="res-btn">
              {home.resButton}
              <span className="res-btn__arrow">→</span>
            </a>

            <div className="res-secondary">
              <span>Or reach us directly</span>
              <div className="res-secondary__sep" />
              <a href={`tel:${settings.phoneRaw}`}>{settings.phone}</a>
              <div className="res-secondary__sep" />
              <a href={`https://wa.me/${settings.whatsappRaw}`} target="_blank" rel="noreferrer">WhatsApp</a>
            </div>
          </div>

        </div>
      </div>

      <ApplyInViewClass sectionRef={sectionRef} inView={inView} />
    </section>
  );
}

function ApplyInViewClass({ sectionRef, inView }) {
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (inView) el.classList.add("in-view");
    else el.classList.remove("in-view");
  }, [inView, sectionRef]);
  return null;
}
